import EnfiniTask from "@/assets/svg/full-logo/task-white.svg";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../ui/button";
import { Slider } from "../ui/slider";

// Hourly rates configuration
const PH_HOURLY_RATE = 6; // Philippines hourly rate
const US_HOURLY_RATE = 30; // US hourly rate
const HOURS_PER_DELIVERABLE = 25; // Estimated hours needed per deliverable

export default function RevenueCalculator() {
    const [monthlyBudget, setMonthlyBudget] = useState(1000);

    // Calculate hours per month based on budget
    const hoursPerMonth = useMemo(() => {
        return monthlyBudget / PH_HOURLY_RATE;
    }, [monthlyBudget]);

    // Calculate hours per week (average 4.33 weeks per month)
    const hoursPerWeek = useMemo(() => {
        return hoursPerMonth / 4.33;
    }, [hoursPerMonth]);

    // Estimate deliverables per month
    const estimatedDeliverables = useMemo(() => {
        const deliverables = Math.floor(hoursPerMonth / HOURS_PER_DELIVERABLE);
        if (deliverables === 0) return "1-2";
        if (deliverables === 1) return "1-2";
        if (deliverables === 2) return "2-3";
        return `${deliverables - 1}-${deliverables + 1}`;
    }, [hoursPerMonth]);

    // Calculate equivalent cost in the West
    const westCost = useMemo(() => {
        return hoursPerMonth * US_HOURLY_RATE;
    }, [hoursPerMonth]);

    // Calculate savings
    const savings = useMemo(() => {
        return westCost - monthlyBudget;
    }, [westCost, monthlyBudget]);

    // Calculate how much further your budget goes
    // This shows the percentage increase beyond what you paid
    // Formula: ((West Cost - Budget) / Budget) * 100
    // Example: Budget $1,000, West Cost $5,000 → (5,000 - 1,000) / 1,000 * 100 = 400%
    // This means your budget goes 400% further (you get 4x more value than you paid)
    const budgetGoesFurther = useMemo(() => {
        return ((westCost - monthlyBudget) / monthlyBudget) * 100;
    }, [westCost, monthlyBudget]);

    const formatCurrency = (amount) => {
        return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    return (
        <div className="px-6 flex flex-col justify-center items-center gap-10 5xl:max-w-[100rem] 5xl:mx-auto">
            <div className="space-y-6 text-center">
                <h1 className="text-center text-3xl md:text-5xl lg:text-4xl xl:text-5xl font-medium">
                    How much of your time are you saving?
                </h1>
                <p className="text-lg">
                    Unlock the full potential of your team. Estimate how much time you could save and boost your productivity with EnfiniTask.
                </p>
            </div>

            <div className="w-full lg:w-[80%] 1xl:w-[70%] mx-auto">
                <div className="bg-[linear-gradient(to_right,var(--primary),var(--tertiary))] rounded-[20px] p-8 md:p-10 lg:p-12 flex flex-col lg:flex-row gap-8 lg:gap-20 1xl:gap-28 text-white">
                    <div className="flex-1 flex flex-col justify-between items-center gap-8">
                        <div className="space-y-6 text-center lg:text-left">
                            <h2 className="text-2xl md:text-3xl font-medium">
                                What would this cost you elsewhere?</h2>
                            <p className=" text-sm md:text-base">
                                Use the slider to discover how much time and output you can gain by optimizing workflows in EnfiniTask.
                            </p>
                        </div>

                        <div className="space-y-4 w-full">
                            <div className="space-y-3">
                                <div className="text-center flex justify-between items-center">
                                    <span className="text-lg">Your monthly budget</span>
                                    <span className="text-xl">
                                        {formatCurrency(monthlyBudget)}
                                    </span>
                                </div>
                                <Slider
                                    value={[monthlyBudget]}
                                    onValueChange={(value) => setMonthlyBudget(value[0])}
                                    min={300}
                                    max={5000}
                                    step={50}
                                    className="[&_[data-slot=slider-range]]:bg-[#9EA0E7] [&_[role=slider]]:bg-[#9EA0E7] [&_[data-slot=slider-track]]:bg-white [&_[role=slider]]:shadow-lg [&_[role=slider]]:size-5 [&_[role=slider]]:hover:size-6 [&_[role=slider]]:transition-all"
                                />
                                <div className="flex justify-between items-center text-white/50">
                                    <span className="text-sm">$300</span>
                                    <span className="text-sm">$2,650</span>
                                    <span className="text-sm">$5,000</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 text-sm md:text-base text-white">
                            <p>Stop letting manual work slow you down. Let EnfiniTask help you streamline, manage, and complete tasks faster.</p>
                            <p>With a dedicated specialist trained specifically for your needs</p>
                        </div>
                    </div>

                    <div className="bg-[#8B5CF6] flex-1 flex flex-col gap-8 rounded-[10px] p-6 lg:p-10">
                        <h2 className="text-xl md:text-2xl font-medium text-white">
                            With EnfiniTask, you get:
                        </h2>

                        <div className="flex flex-col items-start gap-4">
                            <div className="flex gap-2 items-center">
                                <div className="py-1 px-2 bg-[#7C3AED] rounded-md font-semibold">
                                    {Math.round(hoursPerWeek)}
                                </div>
                                <span>hours per week</span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <div className="py-1 px-2 bg-[#7C3AED] rounded-md font-semibold">
                                    {estimatedDeliverables}
                                </div>
                                <span>deliverables per month</span>
                            </div>
                            <div className="flex gap-2 items-center w-full">
                                <span>A dedicated, trained specialist</span>
                            </div>

                            <div className="bg-[#7C3AED]  rounded-[10px] p-6 w-full">
                                <div className="text-xs md:text-sm text-white/50 tracking-wider mb-3">
                                    Equivalent cost in the West
                                </div>
                                <div className="text-3xl md:text-4xl text-white font-medium flex flex-col lg:flex-row items-start lg:items-center gap-4">
                                    <Button className="bg-[#49B935] text-white rounded-xl">Save up to</Button>
                                    {formatCurrency(savings)}/mo
                                </div>
                            </div>
                            <div className="bg-[#7C3AED]  rounded-[10px] p-6 w-full">
                                <div className="text-xs md:text-sm text-white/50 tracking-wider mb-3">
                                    Your budget goes
                                </div>
                                <div className="text-3xl md:text-4xl text-white flex flex-col lg:flex-row gap-2 lg:gap-4">
                                    <span className="mt-auto">
                                        {Math.round(budgetGoesFurther)}%
                                    </span>
                                    <div className="flex items-center gap-3 w-full mt-auto">
                                        <span className="text-white/50 text-base">further with</span>
                                        <img src={EnfiniTask} alt="EnfiniTask" className="w-20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Link to="/hire-a-talent">
                <Button className="group relative rounded-full shadow bg-primary text-white py-6 w-44 flex items-center justify-center gap-2 overflow-hidden hover:text-primary hover:bg-white">
                    <span className="transform transition-transform duration-300 group-hover:translate-x-5">
                        Build your team
                    </span>
                    <ArrowRight className="transform transition-transform duration-300 group-hover:-translate-x-[110px]" />
                </Button>
            </Link>
        </div>
    );
}
